import AsyncStorage from '@react-native-async-storage/async-storage';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { Query } from '@tanstack/react-query';
import type { PersistedClient } from '@tanstack/react-query-persist-client';
import { API_VERSION } from 'app/lib/axios';

const ONE_DAY = 24 * 60 * 60 * 1000;
const PERSISTENCE_SCHEMA_VERSION = 2;
const PERSISTENCE_KEY = 'REACT_QUERY_OFFLINE_CACHE';
const MAX_PERSISTED_BYTES = 1024 * 1024;
const NON_PERSISTED_ROOT_KEYS = new Set(['bff']);

function isInfiniteData(data: unknown): data is { pages: unknown[]; pageParams: unknown[] } {
  return Boolean(
    data &&
    typeof data === 'object' &&
    'pages' in data &&
    'pageParams' in data &&
    Array.isArray((data as { pages?: unknown }).pages) &&
    Array.isArray((data as { pageParams?: unknown }).pageParams),
  );
}

export function shouldPersistQuery(query: Query): boolean {
  if (query.state.status !== 'success' || query.state.isInvalidated) return false;
  const rootKey = query.queryKey[0];
  return typeof rootKey !== 'string' || !NON_PERSISTED_ROOT_KEYS.has(rootKey);
}

function trimInfiniteData(data: unknown): unknown {
  if (!isInfiniteData(data)) return data;
  return {
    ...data,
    pages: data.pages.slice(0, 1),
    pageParams: data.pageParams.slice(0, 1),
  };
}

export function serializeBoundedClient(client: PersistedClient): string {
  const boundedClient: PersistedClient = {
    ...client,
    clientState: {
      ...client.clientState,
      mutations: [],
      queries: client.clientState.queries.map((query) => ({
        ...query,
        state: { ...query.state, data: trimInfiniteData(query.state.data) },
      })),
    },
  };
  const serialized = JSON.stringify(boundedClient);
  if (serialized.length <= MAX_PERSISTED_BYTES) return serialized;

  const queries = [...boundedClient.clientState.queries].sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);
  let low = 0;
  let high = queries.length;
  let largestFittingClient = JSON.stringify({
    ...boundedClient,
    clientState: { ...boundedClient.clientState, queries: [] },
  });

  // Find the largest newest-first prefix that fits without repeatedly
  // serializing every intermediate removal.
  while (low <= high) {
    const midpoint = Math.floor((low + high) / 2);
    const candidate = JSON.stringify({
      ...boundedClient,
      clientState: { ...boundedClient.clientState, queries: queries.slice(0, midpoint) },
    });
    if (candidate.length <= MAX_PERSISTED_BYTES) {
      largestFittingClient = candidate;
      low = midpoint + 1;
    } else {
      high = midpoint - 1;
    }
  }

  return largestFittingClient;
}

export const asyncStoragePersister = createAsyncStoragePersister({
  key: PERSISTENCE_KEY,
  serialize: serializeBoundedClient,
  storage: AsyncStorage,
  throttleTime: 5000,
});

export const queryPersistenceOptions = {
  buster: `${String(PERSISTENCE_SCHEMA_VERSION)}:${API_VERSION}`,
  dehydrateOptions: {
    shouldDehydrateMutation: () => false,
    shouldDehydrateQuery: shouldPersistQuery,
  },
  maxAge: ONE_DAY,
  persister: asyncStoragePersister,
};

export async function clearPersistedQueryCache(): Promise<void> {
  await asyncStoragePersister.removeClient();
}
